import React from 'react';

import { Link } from 'react-router-dom';

import { SEOHead, seoConfig } from '../SEO';

export default function BusinessValuation() {
  return (
    <div className="bg-white">
      <SEOHead {...seoConfig.businessValuation} canonical="/services/business-valuation" />
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 to-black py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-amiva-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amiva-accent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="bg-white/10 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center shadow-lg">
                <span className="w-2 h-2 bg-amiva-accent rounded-full mr-2.5 animate-pulse"></span>
                Expert Business Valuation
              </span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Know Your Business's <br />
              <span className="text-gradient">True Value</span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto mb-10 leading-relaxed">
              Get an accurate, market-based valuation that reflects what buyers are actually paying
              for businesses like yours.
            </p>
            <Link to="/valuation" className="btn-primary px-12 py-5 text-xl inline-block">
              Get Your Free Valuation
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-amiva-primary to-amiva-accent py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-white mb-4 md:mb-0">
              <h3 className="text-xl font-bold mb-1 text-white">Ready to get your valuation?</h3>
              <p className="text-white/90">
                Use our free tool to get an instant business valuation
              </p>
            </div>
            <Link
              to="/valuation"
              className="bg-white text-amiva-primary px-8 py-3 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Try Free Valuation Tool →
            </Link>
          </div>
        </div>
      </div>

      {/* Why Valuation Matters */}
      <div className="py-24 lg:py-32 relative overflow-hidden">
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-amiva-primary/20 to-transparent rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-amiva-accent/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1500ms' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <span className="text-sm font-semibold text-amiva-primary uppercase tracking-wider bg-amiva-primary/10 px-4 py-2 rounded-full">
                The Value of Knowing
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why Business Valuation <span className="text-gradient">Matters</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're planning to sell tomorrow or in five years, knowing your value is the
              first step to maximizing it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Strategic Planning Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amiva-primary/20 to-amiva-primary/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-amiva-primary/20 transform hover:-translate-y-2">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amiva-primary to-purple-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-amiva-primary/30 transition-all duration-300 group-hover:scale-110">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9.5H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-amiva-primary to-purple-600 rounded-2xl animate-ping opacity-20"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center group-hover:text-amiva-primary transition-colors">
                  Strategic Planning
                </h3>
                <p className="text-gray-600 leading-relaxed text-center mb-4">
                  Make informed decisions about growth investments, hiring, and expansion based on how
                  they impact your valuation.
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-gray-500">Data-driven decisions</span>
                </div>
              </div>
            </div>

            {/* Exit Readiness Card */}
            <div className="group relative md:mt-8">
              <div className="absolute inset-0 bg-gradient-to-br from-amiva-accent/20 to-amiva-accent/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-amiva-accent/20 transform hover:-translate-y-2">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amiva-accent to-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-amiva-accent/30 transition-all duration-300 group-hover:scale-110">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-amiva-accent to-emerald-600 rounded-2xl animate-ping opacity-20" style={{ animationDelay: '200ms' }}></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center group-hover:text-amiva-accent transition-colors">
                  Exit Readiness
                </h3>
                <p className="text-gray-600 leading-relaxed text-center mb-4">
                  Understand where you stand today and what specific improvements will have the
                  biggest impact on your sale price.
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm">
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                  <span className="text-gray-500">Maximize your exit value</span>
                </div>
              </div>
            </div>

            {/* Negotiation Power Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amiva-slate/20 to-amiva-slate/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-amiva-slate/20 transform hover:-translate-y-2">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-amiva-slate to-blue-800 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-amiva-slate/30 transition-all duration-300 group-hover:scale-110">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                      <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                    </svg>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-amiva-slate to-blue-800 rounded-2xl animate-ping opacity-20" style={{ animationDelay: '400ms' }}></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center group-hover:text-amiva-slate transition-colors">
                  Negotiation Power
                </h3>
                <p className="text-gray-600 leading-relaxed text-center mb-4">
                  Enter any discussion with confidence, knowing exactly what your business is worth
                  and why.
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                  <span className="text-gray-500">Confidence in every deal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Valuation Process */}
      <div className="py-24 lg:py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-amiva-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amiva-accent/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1000ms' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amiva-primary"></div>
              <span className="mx-4 text-sm font-semibold text-amiva-primary uppercase tracking-wider">Our Process</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amiva-primary"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Valuation <span className="text-gradient">Methodology</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We use multiple approaches to ensure accuracy, combining AI-powered analysis with
              human expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Process Steps with Enhanced Interactivity */}
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute left-6 top-12 bottom-12 w-0.5 bg-gradient-to-b from-amiva-primary via-amiva-accent to-amiva-slate opacity-20"></div>
              
            <div className="space-y-8">
                <div className="group relative flex items-start transition-all duration-300 hover:translate-x-2">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 bg-amiva-primary rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:shadow-amiva-primary/30 transition-all duration-300 group-hover:scale-110">
                  1
                </div>
                    <div className="absolute inset-0 bg-amiva-primary rounded-xl animate-ping opacity-20"></div>
                  </div>
                  <div className="ml-6 flex-1 bg-white p-6 rounded-xl shadow-sm group-hover:shadow-lg transition-all duration-300 border border-gray-100 group-hover:border-amiva-primary/20">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amiva-primary transition-colors">
                          AI-Powered Analysis
                        </h3>
                  <p className="text-gray-600">
                    Our AI analyzes your website to understand your business model, services, target
                    market, and competitive positioning.
                  </p>
                      </div>
                      <svg className="w-6 h-6 text-amiva-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Duration:</span>
                      <span className="text-xs font-semibold text-amiva-primary">30 seconds</span>
                </div>
              </div>
                </div>

                <div className="group relative flex items-start transition-all duration-300 hover:translate-x-2">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 bg-amiva-accent rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:shadow-amiva-accent/30 transition-all duration-300 group-hover:scale-110">
                      2
                    </div>
                    <div className="absolute inset-0 bg-amiva-accent rounded-xl animate-ping opacity-20" style={{ animationDelay: '200ms' }}></div>
                  </div>
                  <div className="ml-6 flex-1 bg-white p-6 rounded-xl shadow-sm group-hover:shadow-lg transition-all duration-300 border border-gray-100 group-hover:border-amiva-accent/20">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amiva-accent transition-colors">
                          Financial Modeling
                        </h3>
                  <p className="text-gray-600">
                    We apply industry-standard multiples based on your revenue, EBITDA, growth rate,
                    and recurring revenue percentage.
                  </p>
                      </div>
                      <svg className="w-6 h-6 text-amiva-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Accuracy:</span>
                      <span className="text-xs font-semibold text-amiva-accent">95%+ confidence</span>
                </div>
              </div>
                </div>

                <div className="group relative flex items-start transition-all duration-300 hover:translate-x-2">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:shadow-blue-600/30 transition-all duration-300 group-hover:scale-110">
                      3
                    </div>
                    <div className="absolute inset-0 bg-blue-600 rounded-xl animate-ping opacity-20" style={{ animationDelay: '400ms' }}></div>
                  </div>
                  <div className="ml-6 flex-1 bg-white p-6 rounded-xl shadow-sm group-hover:shadow-lg transition-all duration-300 border border-gray-100 group-hover:border-blue-600/20">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                          Market Comparables
                        </h3>
                  <p className="text-gray-600">
                    We benchmark against recent transactions in your industry to ensure your
                    valuation reflects current market conditions.
                  </p>
                      </div>
                      <svg className="w-6 h-6 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Data points:</span>
                      <span className="text-xs font-semibold text-blue-600">1000+ transactions</span>
                </div>
              </div>
                </div>

                <div className="group relative flex items-start transition-all duration-300 hover:translate-x-2">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 bg-amiva-slate rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:shadow-amiva-slate/30 transition-all duration-300 group-hover:scale-110">
                      4
                    </div>
                    <div className="absolute inset-0 bg-amiva-slate rounded-xl animate-ping opacity-20" style={{ animationDelay: '600ms' }}></div>
                  </div>
                  <div className="ml-6 flex-1 bg-white p-6 rounded-xl shadow-sm group-hover:shadow-lg transition-all duration-300 border border-gray-100 group-hover:border-amiva-slate/20">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amiva-slate transition-colors">
                    Strategic Value Assessment
                  </h3>
                  <p className="text-gray-600">
                    We identify unique aspects of your business that could command premium
                    valuations from strategic buyers.
                  </p>
                      </div>
                      <svg className="w-6 h-6 text-amiva-slate opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Premium potential:</span>
                      <span className="text-xs font-semibold text-amiva-slate">+15-30% value</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Factors Card */}
            <div className="relative lg:sticky lg:top-8">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100 relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amiva-primary/10 to-transparent rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amiva-accent/10 to-transparent rounded-full blur-2xl"></div>

            <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-2xl font-bold text-gray-900">
                  Valuation Factors We Consider
                </h4>
                    <div className="w-12 h-12 bg-gradient-to-br from-amiva-primary to-amiva-accent rounded-xl flex items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 100 4h2a2 2 0 100 4h2a1 1 0 100 2 2 2 0 01-2 2H4a2 2 0 01-2-2V7a2 2 0 012-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {[
                      { name: 'Revenue Size & Growth', value: 25, color: 'from-purple-500 to-purple-600', icon: '📈' },
                      { name: 'Profitability (EBITDA)', value: 30, color: 'from-emerald-500 to-emerald-600', icon: '💰' },
                      { name: 'Recurring Revenue %', value: 20, color: 'from-blue-500 to-blue-600', icon: '🔄' },
                      { name: 'Market Position', value: 15, color: 'from-amber-500 to-amber-600', icon: '🎯' },
                      { name: 'Strategic Assets', value: 10, color: 'from-rose-500 to-rose-600', icon: '💎' }
                    ].map((factor, index) => (
                      <div 
                        key={index}
                        className="group relative bg-gray-50 rounded-xl p-4 hover:bg-white hover:shadow-lg transition-all duration-300 cursor-pointer border border-transparent hover:border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">{factor.icon}</span>
                            <span className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                              {factor.name}
                            </span>
                          </div>
                          <span className="text-2xl font-bold bg-gradient-to-r from-gray-600 to-gray-800 bg-clip-text text-transparent group-hover:from-amiva-primary group-hover:to-amiva-accent transition-all">
                            {factor.value}%
                          </span>
                        </div>
                        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`absolute inset-y-0 left-0 bg-gradient-to-r ${factor.color} rounded-full transition-all duration-1000 ease-out group-hover:shadow-lg`}
                            style={{ 
                              width: `${factor.value}%`,
                              animationDelay: `${index * 100}ms`
                            }}
                          ></div>
                        </div>
                  </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-amiva-primary/5 to-amiva-accent/5 rounded-xl border border-amiva-primary/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Total Valuation Impact</span>
                      <span className="text-lg font-bold text-amiva-primary">100%</span>
                  </div>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="mt-6 text-center">
                <Link 
                  to="/valuation" 
                  className="inline-flex items-center space-x-2 text-amiva-primary font-semibold hover:text-amiva-accent transition-colors group"
                >
                  <span>See how we'd value your business</span>
                  <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Valuation Methods */}
      <div className="py-24 lg:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amiva-primary"></div>
              <span className="mx-4 text-sm font-semibold text-amiva-primary uppercase tracking-wider">Proven Methods</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amiva-primary"></div>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industry-Standard <span className="text-gradient">Valuation Methods</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We use the right method for your business size and type to ensure accuracy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Revenue Multiple Card */}
            <div className="group relative transform transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-br from-blue-50 to-white p-8">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-blue-600/30 transition-all duration-300">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                      </svg>
                    </div>
                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">Popular</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    Revenue Multiple
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">Best for businesses under $1M EBITDA</p>
                  
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Typical Range</span>
                        <span className="text-lg font-bold text-blue-600">0.5x - 1.2x</span>
                      </div>
                      <div className="w-full bg-blue-100 rounded-full h-2">
                        <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full" style={{ width: '70%' }}></div>
                      </div>
                    </div>
                    
                    <ul className="space-y-3">
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Adjusted for growth rate</span>
                      </li>
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Industry-specific ranges</span>
                      </li>
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Quick valuation method</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <p className="text-white text-center text-sm font-medium">Most common for small businesses</p>
                </div>
              </div>
            </div>

            {/* SDE Multiple Card */}
            <div className="group relative transform transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-br from-emerald-50 to-white p-8">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-emerald-600/30 transition-all duration-300">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="absolute -top-2 -right-2 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">Recommended</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    SDE Multiple
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">Best for owner-operated businesses</p>
                  
                  <div className="space-y-4">
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Typical Range</span>
                        <span className="text-lg font-bold text-emerald-600">2.5x - 4.5x</span>
                      </div>
                      <div className="w-full bg-emerald-100 rounded-full h-2">
                        <div className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                    
                    <ul className="space-y-3">
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Includes owner compensation</span>
                      </li>
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Add-backs considered</span>
                      </li>
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Most accurate for SMBs</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-4">
                  <p className="text-white text-center text-sm font-medium">Preferred by most buyers</p>
                </div>
              </div>
            </div>

            {/* EBITDA Multiple Card */}
            <div className="group relative transform transition-all duration-500 hover:scale-105">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-600 to-amber-800 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                <div className="bg-gradient-to-br from-amber-50 to-white p-8">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-amber-600/30 transition-all duration-300">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="absolute -top-2 -right-2 bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full">Premium</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                    EBITDA Multiple
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">Best for businesses over $1M EBITDA</p>
                  
                  <div className="space-y-4">
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Typical Range</span>
                        <span className="text-lg font-bold text-amber-600">4x - 8x</span>
                      </div>
                      <div className="w-full bg-amber-100 rounded-full h-2">
                        <div className="bg-gradient-to-r from-amber-400 to-amber-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                    </div>
                    
                    <ul className="space-y-3">
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Higher for tech/SaaS</span>
                      </li>
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">Strategic premiums apply</span>
                      </li>
                      <li className="flex items-start group/item">
                        <svg className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-600">PE buyer preferred</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-4">
                  <p className="text-white text-center text-sm font-medium">Best for larger businesses</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-amiva-primary/20 to-black">
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amiva-primary/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amiva-accent/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1000ms' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-amiva-primary/10 to-amiva-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2000ms' }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Animated Badge */}
            <div className="inline-flex items-center justify-center mb-8 fade-in">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                <div className="flex items-center space-x-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="text-white font-medium">Get Your Valuation in 60 Seconds</span>
                </div>
              </div>
            </div>

            {/* Main Heading */}
            <h2 className="text-5xl lg:text-7xl font-bold text-white mb-6 fade-in">
              Ready to Know Your
              <span className="block mt-2 bg-gradient-to-r from-amiva-primary via-amiva-accent to-blue-400 bg-clip-text text-transparent">
                Business Value?
              </span>
            </h2>

            {/* Subheading */}
            <p className="text-xl lg:text-2xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto fade-in">
              Join <span className="font-bold text-white">10,000+ business owners</span> who have discovered their true market value with our AI-powered valuation tool.
            </p>

            {/* Value Props */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12 fade-in">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl mb-2">⚡</div>
                <div className="text-white font-semibold">Instant Results</div>
                <div className="text-gray-400 text-sm">60-second analysis</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl mb-2">🔒</div>
                <div className="text-white font-semibold">100% Confidential</div>
                <div className="text-gray-400 text-sm">Your data is secure</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl mb-2">📊</div>
                <div className="text-white font-semibold">Expert Analysis</div>
                <div className="text-gray-400 text-sm">AI + human insights</div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="fade-in">
              <Link
                to="/valuation"
                className="group relative inline-flex items-center justify-center px-12 py-5 overflow-hidden rounded-2xl bg-gradient-to-r from-amiva-primary to-amiva-accent text-white font-bold text-lg shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-amiva-primary/50"
              >
                <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                <span className="relative flex items-center space-x-3">
                  <span>Start Your Free Valuation</span>
                  <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-400 fade-in">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No credit card required</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No obligations</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Expert insights included</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
